import {
  Box,
  CircularProgress,
  Input,
  Stack,
  Text,
  useMediaQuery,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import { ExpandMore, Search } from '@mui/icons-material';
import { useState } from 'react';

import {
  FilterChip,
  FilterLabel,
  FormContainer,
  MobileSearchButton,
  PillSearchBar,
  SearchButton,
  SearchInputField,
  SearchTypeChip,
  SearchTypeChipsContainer,
  TitleText,
} from '../CourtCases/v2/shared/SearchForm';

import { FilterExpandPanel } from './components';
import { PATENT_STATUS_OPTIONS, PATENT_TYPE_OPTIONS } from './constants';

import type { PatentSearchFilters } from './types';

interface PatentSearchFormProps {
  inputValue: string;
  onInputChange: (_value: string) => void;
  formFilters: PatentSearchFilters;
  onFilterChange: (_key: keyof PatentSearchFilters, _value: string) => void;
  isSearching: boolean;
  onSubmit: (_e: React.FormEvent) => void;
  onClearFilters: () => void;
  searchMode: 'patents' | 'documents';
  onSearchModeChange: (_mode: 'patents' | 'documents') => void;
  documentsAppNumber: string;
  onDocumentsAppNumberChange: (_value: string) => void;
  isDocumentsSearching: boolean;
}

type ActiveFilter =
  | 'type'
  | 'status'
  | 'filingDate'
  | 'grantDate'
  | 'inventor'
  | 'applicant'
  | null;

const FILTER_CHIPS: { key: ActiveFilter; label: string }[] = [
  { key: 'type', label: 'Type' },
  { key: 'status', label: 'Status' },
  { key: 'filingDate', label: 'Filing Date' },
  { key: 'grantDate', label: 'Grant Date' },
  { key: 'inventor', label: 'Inventor' },
  { key: 'applicant', label: 'Applicant' },
];

const hasFilterValue = (
  key: ActiveFilter,
  formFilters: PatentSearchFilters
): boolean => {
  switch (key) {
    case 'type':
      return Boolean(formFilters.applicationType);
    case 'status':
      return Boolean(formFilters.status);
    case 'filingDate':
      return Boolean(formFilters.filingDateFrom && formFilters.filingDateTo);
    case 'grantDate':
      return Boolean(formFilters.grantDateFrom && formFilters.grantDateTo);
    case 'inventor':
      return Boolean(formFilters.inventor);
    case 'applicant':
      return Boolean(formFilters.applicant);
    default:
      return false;
  }
};

export const PatentSearchForm = ({
  inputValue,
  onInputChange,
  formFilters,
  onFilterChange,
  isSearching,
  onSubmit,
  onClearFilters,
  searchMode,
  onSearchModeChange,
  documentsAppNumber,
  onDocumentsAppNumberChange,
  isDocumentsSearching,
}: PatentSearchFormProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>(null);

  const toggleFilter = (key: ActiveFilter) => {
    setActiveFilter((prev) => (prev === key ? null : key));
  };

  const hasAnyFilter = FILTER_CHIPS.some((chip) =>
    hasFilterValue(chip.key, formFilters)
  );

  return (
    <FormContainer component="form" onSubmit={onSubmit} sx={{ mb: 3 }}>
      <TitleText variant="h5">Search Patents</TitleText>
      <Text variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Explore patent filings, inventors, and technical disclosures across
        industries.
      </Text>

      {/* Search type chips */}
      <SearchTypeChipsContainer direction="row">
        <SearchTypeChip
          type="button"
          isSelected={searchMode === 'patents'}
          onClick={() => onSearchModeChange('patents')}
        >
          Patent Search
        </SearchTypeChip>
        <SearchTypeChip
          type="button"
          isSelected={searchMode === 'documents'}
          onClick={() => onSearchModeChange('documents')}
        >
          Documents
        </SearchTypeChip>
      </SearchTypeChipsContainer>

      {searchMode === 'documents' && (
        <>
          <Stack direction="row" alignItems="center" sx={{ mb: 2.5 }}>
            <PillSearchBar>
              <Search
                sx={{
                  fontSize: '1.25rem',
                  color: 'text.secondary',
                  flexShrink: 0,
                }}
              />
              <SearchInputField
                placeholder={
                  isMobile
                    ? 'Application number...'
                    : 'Application number (e.g. 90016030 or 90/016,030)'
                }
                aria-label="Application number"
                value={documentsAppNumber}
                onChange={(e) => onDocumentsAppNumberChange(e.target.value)}
                disabled={isDocumentsSearching}
              />
            </PillSearchBar>
            <SearchButton
              type="submit"
              variant="contained"
              disabled={isDocumentsSearching || !documentsAppNumber.trim()}
              sx={{ height: '48px' }}
            >
              {isDocumentsSearching ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                'Search'
              )}
            </SearchButton>
          </Stack>
          <MobileSearchButton
            type="submit"
            variant="contained"
            disabled={isDocumentsSearching || !documentsAppNumber.trim()}
            sx={{ mb: 2 }}
          >
            {isDocumentsSearching ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Search'
            )}
          </MobileSearchButton>
          <Text variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Enter a patent application number to view associated file-wrapper
            documents.
          </Text>
        </>
      )}

      {searchMode === 'patents' && (
        <>
          {/* Search row */}
          <Stack direction="row" alignItems="center" sx={{ mb: 2.5 }}>
            <PillSearchBar>
              <Search
                sx={{
                  fontSize: '1.25rem',
                  color: 'text.secondary',
                  flexShrink: 0,
                }}
              />
              <SearchInputField
                placeholder={
                  isMobile
                    ? 'Keyword or inventor...'
                    : 'Search by keyword, patent number, inventor...'
                }
                aria-label="Search"
                value={inputValue}
                onChange={(e) => onInputChange(e.target.value)}
                disabled={isSearching}
              />
            </PillSearchBar>
            <SearchButton
              type="submit"
              variant="contained"
              startIcon={
                isSearching ? (
                  <CircularProgress size={18} color="inherit" />
                ) : undefined
              }
              disabled={isSearching || !inputValue.trim()}
              sx={{ height: '48px' }}
            >
              Search
            </SearchButton>
          </Stack>

          {/* Mobile search button */}
          <MobileSearchButton
            type="submit"
            variant="contained"
            disabled={isSearching || !inputValue.trim()}
            sx={{ mb: 2 }}
          >
            {isSearching ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Search'
            )}
          </MobileSearchButton>
        </>
      )}

      {/* Filter chips row — only in patents mode */}
      {searchMode === 'patents' && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          sx={{ rowGap: 1 }}
        >
          <FilterLabel>ADD SEARCH DETAILS:</FilterLabel>

          {FILTER_CHIPS.map(({ key, label }) => {
            const isActive = activeFilter === key;
            const hasValue = hasFilterValue(key, formFilters);
            return (
              <FilterChip
                key={key}
                type="button"
                isActive={isActive || hasValue}
                disabled={isSearching}
                onClick={() => toggleFilter(key)}
                aria-expanded={isActive}
              >
                {label}
                <ExpandMore
                  sx={{
                    fontSize: '1rem',
                    transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s ease-in-out',
                    ml: 0.25,
                  }}
                />
              </FilterChip>
            );
          })}

          {hasAnyFilter && (
            <FilterChip
              type="button"
              isActive={false}
              onClick={() => {
                onClearFilters();
                setActiveFilter(null);
              }}
              disabled={isSearching}
              sx={{ fontSize: '0.75rem' }}
            >
              Clear all
            </FilterChip>
          )}
        </Stack>
      )}

      {/* Expandable filter panels — only in patents mode */}
      {searchMode === 'patents' && (
        <>
          {activeFilter === 'type' && (
            <FilterExpandPanel>
              <Text
                variant="caption"
                sx={{ fontWeight: 700, mb: 1, display: 'block' }}
              >
                Application Type
              </Text>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {PATENT_TYPE_OPTIONS.map((opt) => (
                  <FilterChip
                    key={opt.value}
                    type="button"
                    isActive={
                      formFilters.applicationType === opt.value &&
                      opt.value !== ''
                    }
                    disabled={isSearching}
                    onClick={() => onFilterChange('applicationType', opt.value)}
                  >
                    {opt.label}
                  </FilterChip>
                ))}
              </Stack>
            </FilterExpandPanel>
          )}

          {activeFilter === 'status' && (
            <FilterExpandPanel>
              <Text
                variant="caption"
                sx={{ fontWeight: 700, mb: 1, display: 'block' }}
              >
                Application Status
              </Text>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {PATENT_STATUS_OPTIONS.map((opt) => (
                  <FilterChip
                    key={opt.value}
                    type="button"
                    isActive={
                      formFilters.status === opt.value && opt.value !== ''
                    }
                    disabled={isSearching}
                    onClick={() => onFilterChange('status', opt.value)}
                  >
                    {opt.label}
                  </FilterChip>
                ))}
              </Stack>
            </FilterExpandPanel>
          )}

          {activeFilter === 'filingDate' && (
            <FilterExpandPanel>
              <Text
                variant="caption"
                sx={{ fontWeight: 700, mb: 1.5, display: 'block' }}
              >
                Filing Date Range
              </Text>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Box sx={{ flex: 1 }}>
                  <Text
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: 'block' }}
                  >
                    From
                  </Text>
                  <Input
                    fullWidth
                    size="small"
                    type="date"
                    value={formFilters.filingDateFrom}
                    onChange={(e) =>
                      onFilterChange('filingDateFrom', e.target.value)
                    }
                    disabled={isSearching}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Text
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: 'block' }}
                  >
                    To
                  </Text>
                  <Input
                    fullWidth
                    size="small"
                    type="date"
                    value={formFilters.filingDateTo}
                    onChange={(e) =>
                      onFilterChange('filingDateTo', e.target.value)
                    }
                    disabled={isSearching}
                  />
                </Box>
              </Stack>
            </FilterExpandPanel>
          )}

          {activeFilter === 'grantDate' && (
            <FilterExpandPanel>
              <Text
                variant="caption"
                sx={{ fontWeight: 700, mb: 1.5, display: 'block' }}
              >
                Grant Date Range
              </Text>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Box sx={{ flex: 1 }}>
                  <Text
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: 'block' }}
                  >
                    From
                  </Text>
                  <Input
                    fullWidth
                    size="small"
                    type="date"
                    value={formFilters.grantDateFrom}
                    onChange={(e) =>
                      onFilterChange('grantDateFrom', e.target.value)
                    }
                    disabled={isSearching}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Text
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: 'block' }}
                  >
                    To
                  </Text>
                  <Input
                    fullWidth
                    size="small"
                    type="date"
                    value={formFilters.grantDateTo}
                    onChange={(e) =>
                      onFilterChange('grantDateTo', e.target.value)
                    }
                    disabled={isSearching}
                  />
                </Box>
              </Stack>
            </FilterExpandPanel>
          )}

          {activeFilter === 'inventor' && (
            <FilterExpandPanel>
              <Text
                variant="caption"
                sx={{ fontWeight: 700, mb: 1, display: 'block' }}
              >
                Inventor Name
              </Text>
              <Input
                fullWidth
                size="small"
                placeholder="Enter inventor name"
                aria-label="Inventor"
                value={formFilters.inventor}
                onChange={(e) => onFilterChange('inventor', e.target.value)}
                disabled={isSearching}
              />
            </FilterExpandPanel>
          )}

          {activeFilter === 'applicant' && (
            <FilterExpandPanel>
              <Text
                variant="caption"
                sx={{ fontWeight: 700, mb: 1, display: 'block' }}
              >
                Applicant Name
              </Text>
              <Input
                fullWidth
                size="small"
                placeholder="Enter applicant name"
                aria-label="Applicant"
                value={formFilters.applicant}
                onChange={(e) => onFilterChange('applicant', e.target.value)}
                disabled={isSearching}
              />
            </FilterExpandPanel>
          )}
        </>
      )}
    </FormContainer>
  );
};
